import { useFrame, useThree } from '@react-three/fiber';
import { isTouch } from '../lib/useIsMobile.js';
import { useEffect, useMemo, useRef } from 'react';
import {
  BoxGeometry,
  Color,
  DynamicDrawUsage,
  InstancedMesh,
  Matrix4,
  MeshLambertMaterial,
  Object3D,
  SphereGeometry,
  Vector3,
  type BufferGeometry,
} from 'three';
import { SURAH_PAGES, surahOfBuilding, type Building, type RegionTree } from '@map/shared';
import { containsPoint, type Territory } from '@map/worldkit';

/**
 * The Qur'an as camels, not buildings.
 *
 * "Keep refreshing your knowledge of the Qur'an, for by Him in whose hand my
 * soul is, it escapes faster than camels from their hobbles." So a surah is a
 * camel on its own ground, and neglect is not something burning: it is
 * something getting away. Kept, it kneels hobbled; left a few days it stands
 * and frets; longer and it wanders off its plot; longer still and it is gone,
 * leaving a stake and a snapped rope, with the camel walking away.
 *
 * A surah over five pages of the mushaf is a red camel — the most prized
 * wealth in the hadith. What Tajwid knows about how well it is held dresses
 * it: a saddle blanket once its order is known, a gold headband once it is
 * starred, bells unless it is flagged for revision.
 *
 * Every camel is drawn from a handful of boxes, instanced part by part, so
 * sixty camels cost ten draw calls and each one can still move on its own.
 */
export interface CamelsProps {
  territories: Map<string, Territory>;
  tree: RegionTree;
  buildings: Building[];
  selectedId: string | null;
  /**
   * Clicking a camel opens its surah: the plot it belongs to is selected, which
   * highlights the ground and puts the surah itself in the side panel.
   *
   * Nothing is named in the sky any more. A label under the pointer could only
   * ever say the one thing it had room for, and it said it to a mouse — a
   * finger got nothing at all.
   */
  onOpen?: (regionId: string, buildingId: string) => void;
}

/** Where a camel stands in its own story. Thresholds match the weathering scale. */
const FRETS = 0.12;
const WANDERS = 0.5;
const GONE = 0.8;

/** Pages past which a surah is one of the red camels. */
const RED_PAGES = 5;

const HIDE = new Color('#c8a06a');
const HIDE_RED = new Color('#9c4a2f');
const HIDE_WEATHERED = new Color('#8d8378');
const BLANKET = new Color('#2f5d7c');
const GOLD = new Color('#d8b04a');
const ROPE = new Color('#8a7f6a');

/**
 * The camel in parts, in body-lengths, facing +z and standing on y = 0. Pieces
 * that swing (legs, neck) are modelled so they pivot at the joint rather than
 * about their middle, which is what makes a fold or a stride look right.
 */
const BODY = { w: 0.42, h: 0.34, d: 0.9 };
const LEG = { w: 0.1, h: 0.5, d: 0.1 };
const NECK = { w: 0.15, h: 0.56, d: 0.15 };
const HIPS = [
  [-0.14, 0.28],
  [0.14, 0.28],
  [-0.14, -0.28],
  [0.14, -0.28],
] as const;

interface Camel {
  id: string;
  /** The plot it stands on: what gets selected when it is clicked. */
  regionId: string;
  /** The surah portion this camel is, as the building is named. */
  name: string;
  /** Where its plot is, on the unit sphere. */
  site: Vector3;
  /** Which way it faces, as an angle about its own up. */
  heading: number;
  scale: number;
  weathering: number;
  red: boolean;
  blanket: boolean;
  gold: boolean;
  bells: boolean;
  /** Its own clock, so a field of camels does not move as one animal. */
  phase: number;
  /** Days since it was last read: how far one that got away has gone. */
  quietDays: number;
}

function hash(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return (h >>> 0) / 4294967295;
}

export function Camels({ territories, tree, buildings, selectedId, onOpen }: CamelsProps) {
  const camels = useMemo(() => {
    const out: Camel[] = [];
    for (const building of buildings) {
      const surah = surahOfBuilding(building.name);
      const territory = territories.get(building.regionId);
      if (surah === null || !territory || !tree.regions[building.regionId]) continue;
      const marks = building.marks ?? [];
      out.push({
        id: building.id,
        regionId: building.regionId,
        name: building.name,
        site: new Vector3(territory.site.x, territory.site.y, territory.site.z).normalize(),
        heading: hash(building.id) * Math.PI * 2,
        // Sized to its plot, as the buildings were, but a camel is a small thing
        // on the land rather than a tower.
        scale: Math.min(0.05, Math.max(0.012, Math.sqrt(territory.area) * 0.06)),
        weathering: building.weathering,
        red: (SURAH_PAGES[surah - 1] ?? 0) > RED_PAGES,
        blanket: marks.includes('order'),
        gold: marks.includes('star'),
        bells: !marks.includes('flagged'),
        phase: hash(`${building.id}:phase`) * Math.PI * 2,
        quietDays: building.quietDays ?? 30,
      });
    }
    return out;
  }, [buildings, territories, tree]);

  // The coastlines, so a camel that has walked off the land can be seen to
  // swim rather than stroll across the sea.
  const shores = useMemo(
    () => [...territories.values()].filter((territory) => territory.level === 0),
    [territories],
  );

  /** Where each camel is right now, kept fresh by the frame loop: the middle
   * of its body, which is what you point at, not the ground under its feet. */
  const positions = useRef<Vector3[]>([]);
  const { camera, gl, size } = useThree();

  /**
   * Which camel was clicked, worked out by projecting each one to the screen
   * rather than by firing a ray: a ray never finds them, because every camel is
   * one instance of a shared mesh rather than an object of its own.
   *
   * A click, not a press: dragging the world past a camel is how you get
   * about, and it must never open something.
   */
  useEffect(() => {
    const canvas = gl.domElement;
    const screen = new Vector3();
    const edge = new Vector3();
    const toCamera = new Vector3();
    const pick = (event: { clientX: number; clientY: number }, generosity: number) => {
      const rect = canvas.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      let best: Camel | null = null;
      // Beaten by anything closer; a camel is picked only within its own size.
      let bestDistance = Infinity;
      for (const [index, position] of positions.current.entries()) {
        const camel = camels[index];
        if (!camel) continue;
        // Skip any on the far side of the planet, which the globe hides.
        toCamera.copy(camera.position).sub(position);
        if (toCamera.dot(position) <= 0) continue;
        screen.copy(position).project(camera);
        const sx = ((screen.x + 1) / 2) * rect.width;
        const sy = ((1 - screen.y) / 2) * rect.height;
        const d = Math.hypot(sx - px, sy - py);
        // How big this camel is on screen right now, so the same gesture works
        // zoomed right in and far out: a fixed few pixels was too mean close up
        // and too greedy from orbit.
        edge.copy(position).addScaledVector(position, camel.scale / position.length()).project(camera);
        const ex = ((edge.x + 1) / 2) * rect.width;
        const ey = ((1 - edge.y) / 2) * rect.height;
        const reach =
          Math.min(REACH_MAX, Math.max(REACH_MIN, Math.hypot(ex - sx, ey - sy) * 2.4)) * generosity;
        if (d < reach && d < bestDistance) {
          bestDistance = d;
          best = camel;
        }
      }
      return best;
    };

    // Where the press started and whether it has moved since: a drag that
    // happens to end on a camel is still a drag.
    let from: { x: number; y: number; id: number } | null = null;
    const onDown = (event: PointerEvent) => {
      from = { x: event.clientX, y: event.clientY, id: event.pointerId };
    };
    const onUp = (event: PointerEvent) => {
      const began = from;
      from = null;
      if (!began || began.id !== event.pointerId) return;
      if (Math.hypot(event.clientX - began.x, event.clientY - began.y) > 8) return;
      // A finger is blunter than a pointer, so it is allowed to be further out.
      const camel = pick(event, isTouch() ? 2.4 : 1.6);
      if (camel) onOpen?.(camel.regionId, camel.id);
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', () => (from = null));
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
    };
  }, [camels, camera, gl, size, onOpen]);

  const parts = useMemo(() => buildParts(), []);
  useEffect(() => () => parts.dispose(), [parts]);

  // Reused scratch, so no frame allocates.
  const scratch = useMemo(
    () => ({
      base: new Matrix4(),
      plot: new Matrix4(),
      out: new Matrix4(),
      up: new Vector3(),
      forward: new Vector3(),
      side: new Vector3(),
      axis: new Vector3(),
      east: new Vector3(),
      north: new Vector3(),
    }),
    [],
  );

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    parts.forEachCamel(camels, (camel, index, write) => {
      const gait = gaitOf(camel, time);
      // A camel is not tied to its plot: it walks the planet's surface along a
      // great circle, so one that has got away can end up anywhere on the
      // world rather than sliding off the ground it started on.
      const travelled = gait.strayed * camel.scale;
      standOn(camel.site, camel.heading, 0, camel.scale, GROUND, scratch, scratch.plot);
      // Only one that has walked away can be off its own land.
      const afloat =
        travelled > 0 &&
        !onLand(shores, standOn(camel.site, camel.heading, travelled, 1, 1, scratch, scratch.out));
      standOn(
        camel.site,
        camel.heading,
        travelled,
        camel.scale,
        afloat ? WATER : GROUND,
        scratch,
        scratch.base,
      );
      write(index, camel, gait, afloat, scratch);
      const at = positions.current[index] ?? (positions.current[index] = new Vector3());
      at.setFromMatrixPosition(scratch.base);
      // Up to the middle of the camel, where the eye goes and the pointer aims.
      at.addScaledVector(at, (camel.scale * 0.6) / at.length());
      // Into world space: the globe turns under the camels, and pointing at
      // where they were before it turned finds nothing.
      at.applyMatrix4(parts.group.matrixWorld);
    });
  });

  // Which plot is open is shown by the ground itself; a camel is never lifted
  // and never labelled.
  void selectedId;

  return <primitive object={parts.group} />;
}

const UP = new Vector3(0, 1, 0);
const POLE = new Vector3(0, 0, 1);
/** Land is drawn at 1.02; a camel stands a hair above it, and swims at sea level. */
const GROUND = 1.0205;
const WATER = 1.001;
/** The pointer's reach, in pixels: a camel's own size, kept within reason. */
const REACH_MIN = 14;
const REACH_MAX = 70;

/**
 * Put a camel on the sphere at `from`, facing `heading`, having walked
 * `travelled` radians along that heading. Walking is a rotation about the axis
 * across its path, which keeps it on the surface however far it goes.
 */
function standOn(
  from: Vector3,
  heading: number,
  travelled: number,
  scale: number,
  radius: number,
  scratch: {
    up: Vector3;
    forward: Vector3;
    side: Vector3;
    axis: Vector3;
    east: Vector3;
    north: Vector3;
  },
  into: Matrix4,
): Vector3 {
  const { up, forward, side, axis, east, north } = scratch;
  up.copy(from).normalize();
  // A tangent frame that never collapses: the pole is only used as a
  // reference, and swapped when the camel is standing on it.
  east.crossVectors(Math.abs(up.z) > 0.9 ? UP : POLE, up).normalize();
  north.crossVectors(up, east);
  forward.copy(east).multiplyScalar(Math.cos(heading)).addScaledVector(north, Math.sin(heading));
  axis.crossVectors(up, forward).normalize();
  if (travelled !== 0) {
    up.applyAxisAngle(axis, -travelled).normalize();
    forward.applyAxisAngle(axis, -travelled).normalize();
  }
  side.crossVectors(up, forward).normalize();
  const x = up.x * radius;
  const y = up.y * radius;
  const z = up.z * radius;
  // x across, y up, z the way it faces, so the model's +z is its nose. The
  // basis carries the camel's size; the position stays on the surface.
  into.makeBasis(
    side.multiplyScalar(scale),
    up.multiplyScalar(scale),
    forward.multiplyScalar(scale),
  );
  into.setPosition(x, y, z);
  // Back to the unit sphere: `up` was scaled into the basis above.
  return up.normalize();
}

/** Whether a point on the sphere is on any continent rather than at sea. */
function onLand(shores: Territory[], at: Vector3): boolean {
  for (const shore of shores) if (containsPoint(shore.ring, at)) return true;
  return false;
}

interface Gait {
  /** 0 kneeling, 1 standing. */
  stand: number;
  /** How far the legs swing, in radians. */
  swing: number;
  /** Body-lengths away from its plot. */
  strayed: number;
  /** Bob of the body as it moves. */
  bob: number;
  /** Head sway. */
  sway: number;
  gone: boolean;
}

/**
 * The four states, as movement rather than colour: kneeling and still; standing
 * and fretting; walking its plot; and walking away for good.
 */
function gaitOf(camel: Camel, time: number): Gait {
  const t = time + camel.phase;
  const w = camel.weathering;
  if (w < FRETS) {
    return { stand: 0, swing: 0, strayed: 0, bob: Math.sin(t * 0.6) * 0.01, sway: Math.sin(t * 0.4) * 0.05, gone: false };
  }
  if (w < WANDERS) {
    // Restless: on its feet, shifting its weight, head turning.
    const fret = Math.sin(t * 1.6);
    return { stand: 1, swing: fret * 0.08, strayed: 0, bob: Math.abs(fret) * 0.02, sway: Math.sin(t * 0.9) * 0.28, gone: false };
  }
  if (w < GONE) {
    // Wandering: walking a slow circuit around its own plot.
    const step = t * 1.7;
    const drift = (w - WANDERS) / (GONE - WANDERS);
    return {
      stand: 1,
      swing: Math.sin(step) * 0.3,
      strayed: (0.6 + Math.sin(t * 0.25) * 0.5) * (0.5 + drift),
      bob: Math.abs(Math.sin(step)) * 0.04,
      sway: Math.sin(t * 0.5) * 0.15,
      gone: false,
    };
  }
  // Gone: away at a steady walk, never turning back. How far it has got says
  // how long it has been: a fortnight unread is a camel on the horizon.
  const step = t * 2.6;
  const days = Math.max(0, camel.quietDays - 7);
  return {
    stand: 1,
    swing: Math.sin(step) * 0.42,
    // No wrap: it keeps going, and on a sphere that means around the world.
    strayed: 3 + days * 0.6 + t * 0.12,
    bob: Math.abs(Math.sin(step)) * 0.05,
    sway: Math.sin(t * 0.8) * 0.1,
    gone: true,
  };
}

/**
 * One instanced mesh per part of the camel, each holding every camel's copy of
 * that part. Parts a camel does not wear are parked at zero scale rather than
 * given their own mesh.
 */
function buildParts() {
  const geometries: BufferGeometry[] = [];
  const keep = <T extends BufferGeometry>(g: T): T => {
    geometries.push(g);
    return g;
  };
  // Instance colours come from setColorAt, not a colour attribute: asking for
  // vertex colours with none in the geometry draws every camel black.
  const hide = new MeshLambertMaterial({ flatShading: true });
  const trim = new MeshLambertMaterial({ flatShading: true });
  const mesh = (geometry: BufferGeometry, material: MeshLambertMaterial, count: number, colour?: Color) => {
    const m = new InstancedMesh(geometry, colour ? material.clone() : material, count);
    if (colour) (m.material as MeshLambertMaterial).color.copy(colour);
    m.instanceMatrix.setUsage(DynamicDrawUsage);
    m.frustumCulled = false;
    return m;
  };

  const box = (w: number, h: number, d: number) => keep(new BoxGeometry(w, h, d));
  /** Hangs from y = 0 downwards, so it swings from the hip. */
  const hanging = (w: number, h: number, d: number) => {
    const g = box(w, h, d);
    g.translate(0, -h / 2, 0);
    return g;
  };
  /** Rises from y = 0, so it swings from the shoulder. */
  const rising = (w: number, h: number, d: number) => {
    const g = box(w, h, d);
    g.translate(0, h / 2, 0);
    return g;
  };
  const parts = {
    body: box(BODY.w, BODY.h, BODY.d),
    hump: box(0.3, 0.24, 0.38),
    neck: rising(NECK.w, NECK.h, NECK.d),
    head: box(0.15, 0.16, 0.3),
    leg: hanging(LEG.w, LEG.h, LEG.d),
    blanket: box(BODY.w + 0.08, 0.05, 0.5),
    band: box(0.17, 0.06, 0.17),
    bell: keep(new SphereGeometry(0.045, 6, 5)),
    stake: box(0.05, 0.28, 0.05),
    rope: box(0.025, 0.025, 0.36),
  };

  let meshes: Record<string, InstancedMesh> = {};
  const group = new Object3D();
  const built = { count: 0 };

  const rebuild = (count: number) => {
    for (const m of Object.values(meshes)) {
      group.remove(m);
      m.dispose();
    }
    meshes = {
      body: mesh(parts.body, hide, count),
      hump: mesh(parts.hump, hide, count),
      neck: mesh(parts.neck, hide, count),
      head: mesh(parts.head, hide, count),
      blanket: mesh(parts.blanket, trim, count, BLANKET),
      band: mesh(parts.band, trim, count, GOLD),
      bell: mesh(parts.bell, trim, count, GOLD),
      stake: mesh(parts.stake, trim, count, ROPE),
      rope: mesh(parts.rope, trim, count, ROPE),
    };
    for (let i = 0; i < HIPS.length; i += 1) meshes[`leg${i}`] = mesh(parts.leg, hide, count);
    for (const m of Object.values(meshes)) group.add(m);
    built.count = count;
  };

  const local = new Object3D();
  const compose = (
    name: string,
    index: number,
    base: Matrix4,
    out: Matrix4,
    set: (o: Object3D) => void,
    colour?: Color,
  ) => {
    local.position.set(0, 0, 0);
    local.rotation.set(0, 0, 0);
    local.scale.setScalar(1);
    set(local);
    local.updateMatrix();
    out.multiplyMatrices(base, local.matrix);
    const m = meshes[name];
    if (!m) return;
    m.setMatrixAt(index, out);
    if (colour) m.setColorAt(index, colour);
  };

  return {
    group,
    dispose() {
      for (const m of Object.values(meshes)) m.dispose();
      for (const g of geometries) g.dispose();
      hide.dispose();
      trim.dispose();
    },
    forEachCamel(
      camels: Camel[],
      each: (
        camel: Camel,
        index: number,
        write: (
          index: number,
          camel: Camel,
          gait: Gait,
          afloat: boolean,
          scratch: { base: Matrix4; plot: Matrix4; out: Matrix4 },
        ) => void,
      ) => void,
    ) {
      if (built.count !== camels.length) rebuild(camels.length);
      if (camels.length === 0) return;

      const write = (
        index: number,
        camel: Camel,
        gait: Gait,
        /** Out at sea: only the neck and head show above the water. */
        afloat: boolean,
        scratch: { base: Matrix4; plot: Matrix4; out: Matrix4 },
      ) => {
        const { base, plot, out } = scratch;
        const hideColour = camel.weathering >= GONE ? HIDE_WEATHERED : camel.red ? HIDE_RED : HIDE;
        // Standing, the belly rides at leg height; kneeling, it sits on the ground
        // with the legs folded under — but the neck stays up, as a camel's does.
        // Swimming, the body rides under the surface and the legs paddle out
        // of sight: a camel at sea is a neck and a head crossing the water.
        const belly = afloat ? -0.34 : 0.14 + gait.stand * (LEG.h - 0.04) + gait.bob;
        const fold = afloat ? 0 : 1 - gait.stand;
        const sunk = afloat ? 0 : 1;
        const shoulder = belly + BODY.h * 0.35;
        // Positive tilts the neck forward (rotation about x carries +y to +z).
        const neckAngle = 0.34 - fold * 0.05;
        const neckTop = {
          y: shoulder + Math.cos(neckAngle) * NECK.h,
          z: BODY.d * 0.42 + Math.sin(neckAngle) * NECK.h,
        };

        compose(
          'body',
          index,
          base,
          out,
          (o) => {
            o.position.set(0, belly, 0);
            o.scale.setScalar(sunk);
          },
          hideColour,
        );
        compose(
          'hump',
          index,
          base,
          out,
          (o) => {
            o.position.set(0, belly + BODY.h * 0.6, -0.06);
            o.scale.setScalar(sunk);
          },
          hideColour,
        );
        compose(
          'neck',
          index,
          base,
          out,
          (o) => {
            o.position.set(0, shoulder, BODY.d * 0.42);
            o.rotation.set(neckAngle, gait.sway * 0.4, 0);
          },
          hideColour,
        );
        compose(
          'head',
          index,
          base,
          out,
          (o) => {
            o.position.set(Math.sin(gait.sway) * 0.05, neckTop.y + 0.02, neckTop.z + 0.07);
            o.rotation.set(0.3, gait.sway, 0);
          },
          hideColour,
        );
        for (let i = 0; i < HIPS.length; i += 1) {
          const hip = HIPS[i] as readonly [number, number];
          // Diagonal pairs move together, which is how a camel actually walks.
          const swing = gait.swing * ((i + Math.floor(i / 2)) % 2 === 0 ? 1 : -1);
          compose(
            `leg${i}`,
            index,
            base,
            out,
            (o) => {
              o.position.set(hip[0], belly - BODY.h * 0.35, hip[1]);
              // Folded under the body when kneeling, hidden from the side.
              o.rotation.set(swing + fold * 1.45, 0, 0);
              o.scale.set(sunk, sunk * (1 - fold * 0.35), sunk);
            },
            hideColour,
          );
        }
        compose('blanket', index, base, out, (o) => {
          o.position.set(0, belly + BODY.h * 0.45, -0.02);
          o.scale.setScalar(camel.blanket ? sunk : 0);
        });
        compose('band', index, base, out, (o) => {
          o.position.set(0, neckTop.y + 0.1, neckTop.z + 0.04);
          o.scale.setScalar(camel.gold ? 1 : 0);
        });
        compose('bell', index, base, out, (o) => {
          o.position.set(0, shoulder + NECK.h * 0.35, BODY.d * 0.45);
          o.scale.setScalar(camel.bells ? 1 : 0);
        });
        // What it left behind stays on the plot, however far the camel walks:
        // a stake and a snapped rope, so an empty plot still says something.
        compose('stake', index, plot, out, (o) => {
          o.position.set(0, 0.14, 0);
          o.scale.setScalar(gait.gone ? 1 : 0);
        });
        compose('rope', index, plot, out, (o) => {
          o.position.set(0.05, 0.03, 0.22);
          o.rotation.set(0, 0.6, 0);
          o.scale.setScalar(gait.gone ? 1 : 0);
        });
      };

      camels.forEach((camel, index) => each(camel, index, write));
      for (const m of Object.values(meshes)) {
        m.instanceMatrix.needsUpdate = true;
        if (m.instanceColor) m.instanceColor.needsUpdate = true;

      }
    },
  };
}
